import { Bell, ClipboardList, PackageCheck, UsersRound } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { DataTable } from '../../components/ui/DataTable'
import { PageTitle } from '../../components/ui/PageTitle'
import { api } from '../../lib/api'
import { formatCurrency, formatNumber } from '../../lib/format'

type DashboardData = {
  kpis: {
    totalOrders: { value: number; delta: string }
    revenue: { value: number; delta: string }
    totalCustomers: { value: number; delta: string }
    todayOrders: { value: number; delta: string }
  }
  revenueTrend: { label: string; date: string; revenue: number; orders: number }[]
  recentOrders: {
    id: number
    orderNumber: string
    customer: string
    items: number
    total: number
    payment: string
    status: string
    createdAt: string
  }[]
  ops: {
    topProducts: { id: number; name: string; units: number }[]
    lowStock: { id: number; name: string; stock: number; threshold: number }[]
    todayOrders: number
    deliveryQueue: number
  }
}

const kpiMeta = [
  ['totalOrders', 'Total Orders', ClipboardList, false],
  ['revenue', 'Revenue', PackageCheck, true],
  ['totalCustomers', 'Total Customers', UsersRound, false],
  ['todayOrders', "Today's Orders", Bell, false],
] as const

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

export function DashboardPage() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    setLoading(true)
    api.get('/admin/dashboard')
      .then(({ data }) => setDashboard(data))
      .catch(() => setError('Unable to load dashboard data.'))
      .finally(() => setLoading(false))
  }, [])

  const orderRows = useMemo(() => {
    return (dashboard?.recentOrders ?? []).map(order => [
      order.orderNumber,
      order.customer,
      `${order.items} item${order.items === 1 ? '' : 's'}`,
      formatCurrency(order.total),
      order.payment,
      order.status,
      formatDateTime(order.createdAt),
    ])
  }, [dashboard?.recentOrders])

  return (
    <div className="space-y-6">
      <PageTitle
        title="Dashboard"
        subtitle="Live KPI overview with recent orders, top-selling products, and low-stock alerts."
      />

      {error ? <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}

      {loading ? <DashboardSkeleton /> : dashboard ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {kpiMeta.map(([key, label, IconNode, currency]) => {
              const item = dashboard.kpis[key]
              return (
                <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" key={label}>
                  <div className="flex items-center justify-between">
                    <div className="grid h-11 w-11 place-items-center rounded-lg bg-mango-yellow/25 text-leaf-dark">
                      <IconNode className="h-5 w-5" />
                    </div>
                    <span className="rounded-full bg-leaf/10 px-2.5 py-1 text-xs font-semibold text-leaf-dark">{item.delta}</span>
                  </div>
                  <p className="mt-5 text-sm font-medium text-stone-500">{label}</p>
                  <p className="mt-1 text-3xl font-bold text-stone-950">
                    {currency ? formatCurrency(item.value) : formatNumber(item.value)}
                  </p>
                </section>
              )
            })}
          </div>
          <div className="grid gap-6 xl:grid-cols-[1.4fr_0.8fr]">
            <ChartCard trend={dashboard.revenueTrend} />
            <OpsRail data={dashboard.ops} />
          </div>
          <DataTable
            title="Recent Orders"
            columns={['Order ID', 'Customer', 'Items', 'Amount', 'Payment', 'Status', 'Created']}
            rows={orderRows}
            hrefBase="/orders"
            viewPath={(index) => `/orders/${dashboard.recentOrders[index]?.id}`}
            hideDeleteAction
            hideEditAction
            hideHeaderActions
            hidePagination
          />
        </>
      ) : null}
    </div>
  )
}

function ChartCard({ trend }: { trend: DashboardData['revenueTrend'] }) {
  const maxRevenue = Math.max(...trend.map(item => item.revenue), 1)

  return (
    <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-leaf-dark">Revenue Trend</h2>
        <span className="rounded-lg bg-stone-100 px-3 py-1.5 text-xs font-semibold text-stone-500">Last 12 days</span>
      </div>
      <div className="mt-6 flex h-64 items-end gap-2 rounded-xl bg-gradient-to-b from-mango-yellow/20 to-white p-4">
        {trend.map((item) => {
          const height = Math.max(8, Math.round((item.revenue / maxRevenue) * 100))
          return (
            <div className="group relative min-w-0 flex-1" key={item.date}>
              <div className="rounded-t-md bg-mango-orange transition group-hover:bg-leaf-dark" style={{ height: `${height}%` }} />
              <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 hidden w-max -translate-x-1/2 rounded-lg bg-stone-950 px-2 py-1 text-xs text-white shadow-lg group-hover:block">
                {item.label}: {formatCurrency(item.revenue)} · {item.orders} orders
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function OpsRail({ data }: { data: DashboardData['ops'] }) {
  const cards = [
    ...(data.topProducts.length
      ? data.topProducts.map(product => ['Top seller', product.name, `${product.units} units`])
      : [['Top seller', 'No sales yet', '0 units']]),
    ...(data.lowStock.length
      ? data.lowStock.map(product => ['Low stock', product.name, `${product.stock} left`])
      : [['Low stock', 'Stock is healthy', 'OK']]),
    ["Today's orders", 'Orders placed today', String(data.todayOrders)],
    ['Delivery queue', 'Confirmed / out for delivery', `${data.deliveryQueue} orders`],
  ]

  return (
    <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
      <h2 className="text-lg font-bold text-leaf-dark">Top Products & Low Stock</h2>
      <div className="mt-4 space-y-3">
        {cards.slice(0, 6).map(([label, title, value], index) => (
          <div className="rounded-lg border border-stone-100 bg-stone-50 p-3" key={`${label}-${title}-${index}`}>
            <p className="text-xs font-semibold uppercase text-stone-400">{label}</p>
            <div className="mt-1 flex items-center justify-between gap-3">
              <p className="font-semibold text-stone-900">{title}</p>
              <span className="text-sm font-semibold text-mango-orange">{value}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function DashboardSkeleton() {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" key={index}>
            <div className="flex items-center justify-between">
              <div className="h-11 w-11 animate-pulse rounded-lg bg-stone-100" />
              <div className="h-6 w-16 animate-pulse rounded-full bg-stone-100" />
            </div>
            <div className="mt-5 h-4 w-28 animate-pulse rounded bg-stone-100" />
            <div className="mt-3 h-8 w-24 animate-pulse rounded bg-stone-100" />
          </section>
        ))}
      </div>
      <div className="grid gap-6 xl:grid-cols-[1.4fr_0.8fr]">
        <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <div className="h-5 w-40 animate-pulse rounded bg-stone-100" />
          <div className="mt-6 flex h-64 items-end gap-2 rounded-xl bg-stone-50 p-4">
            {Array.from({ length: 12 }).map((_, index) => (
              <div className="min-w-0 flex-1 animate-pulse rounded-t-md bg-stone-200" key={index} style={{ height: `${30 + (index % 5) * 12}%` }} />
            ))}
          </div>
        </section>
        <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <div className="h-5 w-48 animate-pulse rounded bg-stone-100" />
          <div className="mt-4 space-y-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div className="rounded-lg border border-stone-100 bg-stone-50 p-3" key={index}>
                <div className="h-3 w-20 animate-pulse rounded bg-stone-200" />
                <div className="mt-3 h-4 w-full animate-pulse rounded bg-stone-200" />
              </div>
            ))}
          </div>
        </section>
      </div>
      <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
        <div className="h-5 w-32 animate-pulse rounded bg-stone-100" />
        <div className="mt-5 space-y-3">
          {Array.from({ length: 5 }).map((_, index) => (
            <div className="h-12 animate-pulse rounded-lg bg-stone-100" key={index} />
          ))}
        </div>
      </section>
    </>
  )
}
